/**
 * 正式相册（/album、/album/:photoId，P3，用户 10-03「做正式的相册页」；c84a 多级页面单 L2 回忆 / L3 照片详情）的纯函数：
 * 查询键、按日子分组、日子与当地时间的说法、相册里摆得出来的照片。页面见 AlbumPage / AlbumPhotoPage。
 * - 相册只放画好的照片（后端默认网格：已成图、没移除、不是幻想主题的）；在画的、没画成的、结果没确认的在「拍照记录」（/photos），不叫照片。
 * - 日子用服务端按宠物日册时区算好的 album_day 分组，不用浏览器时区；当地时间按拍摄地时区，缺就写「当地时间待核」，不拿本机或香港补。
 */
import type { PetPhoto, PhotoCategory } from "@/shared/contracts";

/** 一页取几张（后端上限 60） */
export const ALBUM_PAGE = 24;
/** 「想象出来的冒险」那一叠一页取几张 */
export const FANTASY_PAGE = 12;

export const albumBookKey = (userId: string, petId: string, category: PhotoCategory | "default") => ["pets", "album-book", userId, petId, category] as const;
export const albumPhotoKey = (userId: string, petId: string, photoId: string) => ["pets", "album-photo", userId, petId, photoId] as const;
/** 回忆页「照片」卡上的张数：只要第一页的 total_visible */
export const albumCountKey = (userId: string, petId: string) => ["pets", "album-count", userId, petId] as const;

/** 摆得出来的：画好了、有地址的图片或视频（相册接口本来只给画好的，这里再兜一层，不摆空框） */
export function shown(photo: PetPhoto): boolean {
  return Boolean(photo.asset.media_url) && (photo.asset.kind === "image" || photo.asset.kind === "video");
}

/** 是不是想象出来的那一类（幻想主题、虚构冒险）：详情页明说「不算 TA 真的去过」 */
export function imagined(photo: PetPhoto): boolean {
  return photo.category === "fantasy" || photo.narrative === "fictional_adventure";
}

/** 拍在哪：地点名、城市都有时两样都说；地点名里已经带着城市（同名，或“香港·中环”这样）只说地点名；都没有就不说 */
export function placeLine(photo: PetPhoto): string | null {
  const { label, city } = photo.place;
  if (label && city && !label.includes(city)) return `${label} · ${city}`;
  return label ?? city ?? null;
}

export const UNDATED = "日子没记下";

const WEEKDAY = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

/**
 * 日子的说法：「10 月 3 日 · 周五」，不是今年的带上年份。按日期本身算星期（UTC 正午，和浏览器时区无关）。
 * 认不出的写法原样不显示，归到「日子没记下」。
 */
export function dayText(day: string | null, thisYear: number): string {
  const m = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  if (!m) return UNDATED;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const weekday = WEEKDAY[new Date(Date.UTC(y, mo - 1, d, 12)).getUTCDay()];
  return `${y === thisYear ? "" : `${y} 年 `}${mo} 月 ${d} 日 · ${weekday}`;
}

export interface AlbumDay {
  /** 分组键：album_day，没有就是空 */
  day: string | null;
  photos: PetPhoto[];
}

/** 按 album_day 分组，组的先后照接口顺序（后端已按拍摄时刻倒序）；没有日子的放最后一组 */
export function groupByDay(photos: readonly PetPhoto[]): AlbumDay[] {
  const groups = new Map<string | null, PetPhoto[]>();
  for (const photo of photos) {
    const key = photo.album_day ?? null;
    const list = groups.get(key);
    if (list) list.push(photo);
    else groups.set(key, [photo]);
  }
  const dated = [...groups.entries()].filter(([day]) => day !== null).map(([day, list]) => ({ day, photos: list }));
  const undated = groups.get(null);
  return undated ? [...dated, { day: null, photos: undated }] : dated;
}

/** 当地时间：拍摄地时区里的「14:05」；没有时区或认不出写「当地时间待核」 */
export function localTimeText(photo: PetPhoto): string {
  const zone = photo.place.timezone;
  const at = Date.parse(photo.captured_at);
  if (!zone || Number.isNaN(at)) return "当地时间待核";
  try {
    const time = new Intl.DateTimeFormat("zh-CN", { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(at);
    return `当地 ${time}`;
  } catch {
    return "当地时间待核";
  }
}

/** 读屏念的一张照片：配的话在前，再说拍在哪；都没有就说「一张照片」 */
export function photoLabel(photo: PetPhoto): string {
  const place = placeLine(photo);
  const kind = photo.asset.kind === "video" ? "视频" : "照片";
  const head = place ? `在${place.replace(" · ", "，")}拍的${kind}` : `一张${kind}`;
  return photo.caption ? `${head}：${photo.caption}` : head;
}
